import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AlertCircle, CheckCircle, Info, X } from 'lucide-react';

export type ToastVariant = 'success' | 'error' | 'info';

interface ToastOptions {
  duration?: number;
}

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastContextValue {
  showToast: (message: string, variant?: ToastVariant, options?: ToastOptions) => number;
  success: (message: string, options?: ToastOptions) => number;
  error: (message: string, options?: ToastOptions) => number;
  info: (message: string, options?: ToastOptions) => number;
  dismiss: (id: number) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const COLORS: Record<ToastVariant, { background: string; border: string; icon: string }> = {
  success: { background: '#ecfdf5', border: '#6ee7b7', icon: '#059669' },
  error: { background: '#fef2f2', border: '#fca5a5', icon: '#dc2626' },
  info: { background: '#eff6ff', border: '#93c5fd', icon: '#2563eb' },
};

const ToastIcon = ({ variant }: { variant: ToastVariant }) => {
  const props = { size: 18, color: COLORS[variant].icon, 'aria-hidden': true };
  if (variant === 'success') return <CheckCircle {...props} />;
  if (variant === 'error') return <AlertCircle {...props} />;
  return <Info {...props} />;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const showToast = useCallback((
    message: string,
    variant: ToastVariant = 'info',
    options: ToastOptions = {},
  ) => {
    const id = nextId.current++;
    const duration = options.duration ?? (variant === 'error' ? 7000 : 4500);
    setToasts((current) => [...current.slice(-3), { id, message, variant }]);
    if (duration > 0) {
      timers.current.set(id, setTimeout(() => dismiss(id), duration));
    }
    return id;
  }, [dismiss]);

  useEffect(() => () => {
    timers.current.forEach(clearTimeout);
    timers.current.clear();
  }, []);

  const value = useMemo<ToastContextValue>(() => ({
    showToast,
    success: (message, options) => showToast(message, 'success', options),
    error: (message, options) => showToast(message, 'error', options),
    info: (message, options) => showToast(message, 'info', options),
    dismiss,
  }), [dismiss, showToast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        aria-label="Notifications"
        style={{
          position: 'fixed',
          top: '1rem',
          right: '1rem',
          zIndex: 12000,
          display: 'flex',
          flexDirection: 'column',
          gap: '0.625rem',
          width: 'min(24rem, calc(100vw - 2rem))',
          pointerEvents: 'none',
        }}
      >
        {toasts.map((toast) => (
          <div
            key={toast.id}
            role={toast.variant === 'error' ? 'alert' : 'status'}
            aria-live={toast.variant === 'error' ? 'assertive' : 'polite'}
            aria-atomic="true"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '0.625rem',
              padding: '0.75rem',
              borderRadius: '8px',
              border: `1px solid ${COLORS[toast.variant].border}`,
              backgroundColor: COLORS[toast.variant].background,
              color: '#1f2937',
              boxShadow: '0 10px 25px rgba(15, 23, 42, 0.15)',
              pointerEvents: 'auto',
            }}
          >
            <span style={{ display: 'flex', marginTop: '1px', flexShrink: 0 }}>
              <ToastIcon variant={toast.variant} />
            </span>
            <span style={{ flex: 1, fontSize: '0.875rem', lineHeight: 1.45 }}>
              {toast.message}
            </span>
            <button
              type="button"
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
              style={{
                display: 'flex',
                padding: '2px',
                border: 0,
                background: 'transparent',
                color: '#4b5563',
                cursor: 'pointer',
                borderRadius: '4px',
              }}
            >
              <X size={16} aria-hidden="true" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};

export const useToast = (): ToastContextValue => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
};
